import React, { useContext } from 'react';
import { graphql } from 'gatsby';
import * as styles from './sauna-comparison-chart.module.css';
import {
    BrandColor,
    CTALocation,
    KontentMedia,
    KontentRadioMultiChoice,
    KontentRichText,
    KontentStringValue,
} from '../../../types/KontentProps';
import { useBreakpoint } from 'gatsby-plugin-breakpoints';
import { getLangPrefix } from '../../global/countries';
import {
    getColor,
    getMediaData,
    getMultiChoice,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
} from '../Global/DataUtils';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import { PageMetadataContext } from '../../templates/PageMetaDataContext';
import RichText from '../Global/RichText';

type WoodTypes = {
    elements: {
        image: KontentMedia;
        name_for_display: KontentStringValue;
    };
};

type SaunaInfoArr = {
    elements: {
        sauna_name: KontentRichText;
        sauna_image: KontentMedia;
        image_link?: CTALocation;
    };
};

type cells = {
    elements: {
        text: KontentStringValue;
        wood_type: { value: WoodTypes[] };
        cta_location_anchor_point: KontentStringValue;
        cta_window_action: KontentRadioMultiChoice<
            'new_window' | 'same_window'
        >;
        cta_location: CTALocation;
    };
};

type Rows = {
    elements: {
        comparison_aspect: KontentStringValue;
        row_type: KontentRadioMultiChoice<'text' | 'cta' | 'wood_type'>;
        cell_values: { value: cells[] };
    };
};

interface Props {
    kontentItemId?: string;
    sauna_comparison_info: { value: SaunaInfoArr[] };
    sauna_comparison_row: { value: Rows[] };
    cell_background_color?: BrandColor;
    cell_font_color?: BrandColor;
}

const ComparisonCell: React.FC<{
    rowType: string;
    cell: cells;
    preferredLanguage: string;
}> = ({ rowType, cell, preferredLanguage }) => {
    if (rowType === 'wood_type') {
        const woods = cell?.elements.wood_type.value.map((wood) => ({
            img: wood.elements.image,
            name: wood.elements.name_for_display,
        }));
        return (
            <td className={styles.tableData}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {woods?.map((wood, idx) => (
                        <div key={idx}>
                            <div data-kontent-element-codename="image">
                                <img
                                    src={getMediaData(wood.img)?.url}
                                    className={styles.woodIcon}
                                    alt={wood.name?.value || 'Wood type'}
                                    loading="lazy"
                                    decoding="async"
                                />
                            </div>
                            <span data-kontent-element-codename="name_for_display">
                                {wood.name?.value || 'Wood'}
                            </span>
                        </div>
                    ))}
                </div>
            </td>
        );
    }

    if (rowType === 'cta') {
        let linkHref = BuildLinkHref(
            cell?.elements.cta_location,
            cell?.elements.cta_location_anchor_point
        );

        linkHref = linkHref
            ? `${getLangPrefix(preferredLanguage)}${linkHref}`
            : '';

        const ctaWindowAction =
            getMultiChoice(cell?.elements.cta_window_action) === 'new_window'
                ? '_blank'
                : undefined;

        return (
            <td className={styles.tableData}>
                <div
                    className={styles.secondaryCtaStyle}
                    style={{ color: 'var(--color-orange)' }}
                >
                    <a
                        href={linkHref}
                        target={ctaWindowAction}
                        rel={
                            ctaWindowAction === '_blank'
                                ? 'noopener noreferrer'
                                : undefined
                        }
                    >
                        <h5>{cell?.elements.text.value + ' >' || '----'}</h5>
                    </a>
                </div>
            </td>
        );
    }

    // default: text
    return (
        <td className={styles.tableData}>
            {cell?.elements.text.value || '----'}
        </td>
    );
};

const ComparisonRow: React.FC<{
    row: Rows;
    preferredLanguage: string;
}> = ({ row, preferredLanguage }) => {
    const rowType = getMultiChoice(row.elements.row_type);

    return (
        <tr>
            <td className={styles.tableData}>
                <strong
                    style={{ margin: '0', fontSize: '19px' }}
                    data-kontent-element-codename="comparison_aspect"
                >
                    {row.elements.comparison_aspect.value}
                </strong>
            </td>
            {row.elements.cell_values.value.map((cell, colIndex) => (
                <ComparisonCell
                    key={colIndex}
                    rowType={rowType}
                    cell={cell}
                    preferredLanguage={preferredLanguage}
                />
            ))}
        </tr>
    );
};

const SaunaComparisonChart: React.FC<Props> = ({
    kontentItemId,
    sauna_comparison_info,
    sauna_comparison_row,
    cell_background_color,
    cell_font_color,
}) => {
    const cellBackground =
        getColor(cell_background_color) || 'var(--color-gray)';
    const cellfontColor = getColor(cell_font_color) || 'var(--color-dark-gray)';

    const pageMetadata = useContext(PageMetadataContext);
    const preferredLanguage = pageMetadata.preferredLanguage || 'default';

    const totalColumns = sauna_comparison_info.value.length + 1;
    const breakpoints = useBreakpoint();

    return (
        <>
            <div
                style={
                    breakpoints.xl && totalColumns >= 5
                        ? { margin: '4rem' }
                        : breakpoints.md && !breakpoints.xl && totalColumns >= 5
                        ? { margin: '4rem 20%' }
                        : {}
                }
                className={styles.wrapper}
                data-kontent-item-id={kontentItemId}
            >
                <style>{`
          ${
              breakpoints.md && !breakpoints.xl && totalColumns >= 4
                  ? `.${styles.tableData} { min-width: 18vw !important; }`
                  : totalColumns >= 4 && breakpoints.xl
                  ? `.${styles.tableData} { margin: 4rem 10vw; }`
                  : ''
          }
          .${styles.tableData} {
            background-color: ${cellBackground};
            color: ${cellfontColor};
          }
          @media screen and (min-width: 1550px) {
            .${styles.DesktopCompareAspect} thead th:first-child,
            .${styles.DesktopCompareAspect} tbody td:first-child {
              max-width: 10%;
            }
          }
        `}</style>

                {/* Mobile */}
                {!breakpoints.md || (!breakpoints.xl && totalColumns >= 5) ? (
                    <div>
                        {sauna_comparison_info.value.map((col, idx) => {
                            let imageLinkHref = BuildLinkHref(
                                col.elements.image_link
                            );
                            imageLinkHref = imageLinkHref
                                ? `${getLangPrefix(
                                      preferredLanguage
                                  )}${imageLinkHref}`
                                : '';
                            const imageContent = (
                                <img
                                    src={optimizeKontentImageUrl(
                                        getMediaData(col.elements.sauna_image)
                                            ?.url,
                                        {
                                            ...imagePreset.card.desktop,
                                            q: 95,
                                        }
                                    )}
                                    srcSet={buildKontentSrcSet(
                                        getMediaData(col.elements.sauna_image)
                                            ?.url,
                                        imagePreset.card.desktop.w,
                                        { fm: 'webp' as const, q: 95 }
                                    )}
                                    sizes="(max-width: 768px) 100vw, 33vw"
                                    alt={col.elements.sauna_name?.value || ''}
                                    loading="lazy"
                                    decoding="async"
                                />
                            );
                            return (
                                <div key={idx} style={{ marginBottom: '2rem' }}>
                                    {imageLinkHref ? (
                                        <a
                                            href={imageLinkHref}
                                            className={`${styles.imageLink} ${styles.mobileImg}`}
                                            data-has-image-link
                                            data-kontent-element-codename="sauna_image"
                                            aria-label={`View ${
                                                col.elements.sauna_name
                                                    ?.value || 'sauna'
                                            } details`}
                                        >
                                            {imageContent}
                                        </a>
                                    ) : (
                                        <div
                                            className={styles.mobileImg}
                                            data-kontent-element-codename="sauna_image"
                                        >
                                            {imageContent}
                                        </div>
                                    )}

                                    <p className={styles.mobileSaunaName}>
                                        {RichText(
                                            col.elements.sauna_name,
                                            false,
                                            undefined,
                                            undefined,
                                            'sauna_name'
                                        )}
                                    </p>

                                    <table
                                        className={`${styles.table} ${styles.mobileColumn}`}
                                    >
                                        <tbody>
                                            {sauna_comparison_row.value.map(
                                                (row, rowIndex) => {
                                                    const cell =
                                                        row.elements.cell_values
                                                            .value[idx];
                                                    const rowType =
                                                        getMultiChoice(
                                                            row.elements
                                                                .row_type
                                                        );

                                                    return (
                                                        <tr key={rowIndex}>
                                                            <td
                                                                className={
                                                                    styles.tableData
                                                                }
                                                            >
                                                                <strong
                                                                    style={{
                                                                        margin: '0',
                                                                        fontSize:
                                                                            '1rem',
                                                                    }}
                                                                    data-kontent-element-codename="comparison_aspect"
                                                                >
                                                                    {
                                                                        row
                                                                            .elements
                                                                            .comparison_aspect
                                                                            .value
                                                                    }
                                                                </strong>
                                                            </td>
                                                            <ComparisonCell
                                                                rowType={
                                                                    rowType
                                                                }
                                                                cell={cell}
                                                                preferredLanguage={
                                                                    preferredLanguage
                                                                }
                                                            />
                                                        </tr>
                                                    );
                                                }
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    /* Desktop */
                    <div
                        className={styles.tableContainer}
                        style={
                            breakpoints.xl && totalColumns <= 5
                                ? { padding: '0 9vw' }
                                : {}
                        }
                    >
                        <table
                            className={`${styles.table} ${
                                totalColumns <= 4
                                    ? styles.mobileColumn
                                    : styles.DesktopCompareAspect
                            }`}
                        >
                            <thead>
                                <tr>
                                    <th></th>
                                    {sauna_comparison_info.value.map(
                                        (col, index) => {
                                            let imageLinkHref = BuildLinkHref(
                                                col.elements.image_link
                                            );
                                            imageLinkHref = imageLinkHref
                                                ? `${getLangPrefix(
                                                      preferredLanguage
                                                  )}${imageLinkHref}`
                                                : '';
                                            const desktopImage = (
                                                <img
                                                    src={optimizeKontentImageUrl(
                                                        getMediaData(
                                                            col.elements
                                                                .sauna_image
                                                        )?.url,
                                                        {
                                                            ...imagePreset.card
                                                                .desktop,
                                                            q: 95,
                                                        }
                                                    )}
                                                    srcSet={buildKontentSrcSet(
                                                        getMediaData(
                                                            col.elements
                                                                .sauna_image
                                                        )?.url,
                                                        imagePreset.card.desktop
                                                            .w,
                                                        {
                                                            fm: 'webp' as const,
                                                            q: 95,
                                                        }
                                                    )}
                                                    sizes="(max-width: 768px) 100vw, 33vw"
                                                    alt={
                                                        col.elements.sauna_name
                                                            ?.value || ''
                                                    }
                                                    loading="lazy"
                                                    decoding="async"
                                                />
                                            );
                                            return (
                                                <th key={index}>
                                                    <span
                                                        className={
                                                            styles.columnName
                                                        }
                                                        style={
                                                            totalColumns <= 6 &&
                                                            breakpoints.xl
                                                                ? {
                                                                      fontSize:
                                                                          '1.2rem',
                                                                  }
                                                                : {}
                                                        }
                                                    >
                                                        {imageLinkHref ? (
                                                            <a
                                                                href={
                                                                    imageLinkHref
                                                                }
                                                                className={
                                                                    styles.imageLink
                                                                }
                                                                data-has-image-link
                                                                data-kontent-element-codename="sauna_image"
                                                                aria-label={`View ${
                                                                    col.elements
                                                                        .sauna_name
                                                                        ?.value ||
                                                                    'sauna'
                                                                } details`}
                                                            >
                                                                {desktopImage}
                                                            </a>
                                                        ) : (
                                                            <div data-kontent-element-codename="sauna_image">
                                                                {desktopImage}
                                                            </div>
                                                        )}
                                                    </span>
                                                </th>
                                            );
                                        }
                                    )}
                                </tr>
                            </thead>
                            <thead>
                                <tr>
                                    <th></th>
                                    {sauna_comparison_info.value.map(
                                        (col, index) => {
                                            return (
                                                <th
                                                    key={index}
                                                    style={{
                                                        height: 'fit-content',
                                                    }}
                                                >
                                                    <span
                                                        className={`${
                                                            styles.columnName
                                                        } ${
                                                            totalColumns <= 6 &&
                                                            breakpoints.xl
                                                                ? styles.SaunaNameDesktopStyles
                                                                : ''
                                                        }`}
                                                        style={
                                                            totalColumns <= 6 &&
                                                            breakpoints.xl
                                                                ? {
                                                                      fontSize:
                                                                          '22px',
                                                                      wordBreak:
                                                                          'normal',
                                                                  }
                                                                : {}
                                                        }
                                                    >
                                                        {RichText(
                                                            col.elements
                                                                .sauna_name,
                                                            false,
                                                            undefined,
                                                            undefined,
                                                            'sauna_name'
                                                        )}
                                                    </span>
                                                </th>
                                            );
                                        }
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {sauna_comparison_row.value.map(
                                    (row, rowIndex) => (
                                        <ComparisonRow
                                            key={rowIndex}
                                            row={row}
                                            preferredLanguage={
                                                preferredLanguage
                                            }
                                        />
                                    )
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
};

export default SaunaComparisonChart;

export const fragmentSaunaComparisonChart = graphql`
    fragment SaunaComparisonChart on kontent_item_component___sauna_comparison_chart {
        elements {
            sauna_comparison_info {
                value {
                    ... on kontent_item_content_item___sauna_comparison_info {
                        elements {
                            sauna_name {
                                ...kontentRichText
                            }
                            sauna_image {
                                ...kontentImage
                            }
                            image_link {
                                ...kontentCtaLocation
                            }
                        }
                    }
                }
            }
            cell_background_color {
                ...kontentColor
            }
            cell_font_color {
                ...kontentColor
            }

            sauna_comparison_row {
                value {
                    ... on kontent_item_content_item___sauna_comparison_row {
                        id
                        elements {
                            comparison_aspect {
                                value
                            }
                            row_type {
                                value {
                                    codename
                                }
                            }
                            cell_values {
                                value {
                                    ... on kontent_item_content_item___sauna_comparison_cell_value {
                                        id
                                        elements {
                                            text {
                                                value
                                            }
                                            wood_type {
                                                value {
                                                    ... on kontent_item_data___material {
                                                        id
                                                        elements {
                                                            image {
                                                                ...kontentImage
                                                            }
                                                            name_for_display {
                                                                value
                                                            }
                                                        }
                                                    }
                                                }
                                            }
                                            cta_location_anchor_point {
                                                value
                                            }
                                            cta_window_action {
                                                value {
                                                    codename
                                                }
                                            }
                                            cta_location {
                                                ...kontentCtaLocation
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`;
